import React, { createContext, useState, useCallback, ReactNode, useEffect } from 'react';
import { apiService } from '../services/apiService';
import { SafeUser } from '../types';

const AUTH_USER_KEY = 'receiptApp.user';
const AUTH_ACCESS_TOKEN_KEY = 'receiptApp.accessToken';
const AUTH_REFRESH_TOKEN_KEY = 'receiptApp.refreshToken';


export interface AuthContextType {
    user: SafeUser | null;
    isAuthenticated: boolean;
    login: (username: string, password: string) => Promise<boolean>;
    logout: () => void;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<SafeUser | null>(() => {
        try {
            const storedUser = sessionStorage.getItem(AUTH_USER_KEY);
            const storedAccessToken = sessionStorage.getItem(AUTH_ACCESS_TOKEN_KEY);
            const storedRefreshToken = sessionStorage.getItem(AUTH_REFRESH_TOKEN_KEY);
            return storedUser && storedAccessToken && storedRefreshToken ? JSON.parse(storedUser) : null;
        } catch {
            return null;
        }
    });

    const login = useCallback(async (username: string, password: string): Promise<boolean> => {
        try {
            const response = await apiService.login(username, password);

            if (response) {
                sessionStorage.setItem(AUTH_USER_KEY, JSON.stringify(response.user));
                sessionStorage.setItem(AUTH_ACCESS_TOKEN_KEY, response.accessToken);
                sessionStorage.setItem(AUTH_REFRESH_TOKEN_KEY, response.refreshToken);
                setUser(response.user);
                return true;
            }
        } catch (error) {
            console.error("Chyba při přihlašování:", error);
        }
        return false;
    }, []);

    const logout = useCallback(() => {
        sessionStorage.removeItem(AUTH_USER_KEY);
        sessionStorage.removeItem(AUTH_ACCESS_TOKEN_KEY);
        sessionStorage.removeItem(AUTH_REFRESH_TOKEN_KEY);
        setUser(null);
    }, []);

    useEffect(() => {
        const handleAuthExpired = () => {
            logout();
        };

        window.addEventListener('auth-expired', handleAuthExpired);

        return () => {
            window.removeEventListener('auth-expired', handleAuthExpired);
        };
    }, [logout]);


    const value: AuthContextType = {
        user,
        isAuthenticated: !!user,
        login,
        logout,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};